import { Play, X } from 'lucide-react'
import type React from 'react'
import { useState } from 'react'
import type { VideoSliderProps } from '@/types/components'

export const VideoSlider: React.FC<VideoSliderProps> = ({ videos, title = 'Videos' }) => {
  const [activeVideo, setActiveVideo] = useState<string | null>(null)
  const [isPaused, setIsPaused] = useState(false)

  if (videos.length === 0) return null

  // Double the videos for seamless infinite loop
  const loopedVideos = [...videos, ...videos]

  return (
    <>
      <div className="w-full">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">{title}</h3>

        <div
          className="overflow-hidden"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          <div
            className="flex gap-4"
            style={{
              animation: `video-marquee ${videos.length * 5}s linear infinite`,
              animationPlayState: isPaused || activeVideo ? 'paused' : 'running',
              width: 'max-content',
            }}
          >
            {loopedVideos.map((video, index) => (
              <button
                key={`${video.id}-${index}`}
                type="button"
                onClick={() => setActiveVideo(video.id)}
                className="flex-shrink-0 w-[280px] sm:w-[320px] group cursor-pointer"
              >
                <div className="relative aspect-video rounded-lg overflow-hidden bg-gray-900">
                  <img
                    src={`https://img.youtube.com/vi/${video.id}/mqdefault.jpg`}
                    alt={video.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300 opacity-80 group-hover:opacity-100"
                  />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-12 h-12 rounded-full bg-primary/90 group-hover:bg-primary flex items-center justify-center shadow-lg group-hover:scale-110 transition-all duration-300">
                      <Play className="h-5 w-5 text-white ml-0.5" fill="white" />
                    </div>
                  </div>
                </div>
                <p className="mt-2 text-sm font-medium text-gray-700 group-hover:text-primary transition-colors text-left">
                  {video.title}
                </p>
              </button>
            ))}
          </div>
        </div>
      </div>

      {activeVideo && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
          onClick={() => setActiveVideo(null)}
          role="dialog"
        >
          <div
            className="relative w-full max-w-4xl aspect-video"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setActiveVideo(null)}
              className="absolute -top-10 right-0 text-white hover:text-gray-300 transition-colors"
            >
              <X className="h-8 w-8" />
            </button>
            <iframe
              src={`https://www.youtube.com/embed/${activeVideo}?autoplay=1`}
              title="Video"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="w-full h-full rounded-lg"
            />
          </div>
        </div>
      )}
    </>
  )
}
